import { TELANGANA_DISTRICTS, type TelanganaDistrictKey } from "./telanganaDistricts";
import { TELANGANA_MANDAL_HOTSPOTS } from "./telanganaMandalHotspots";

export type MandalHotspot = { title: string; d: string };

/** Canonical hotspot bundle keys in `telanganaMandalHotspots.ts` per district slug. */
const MANDAL_HOTSPOT_KEY_BY_DISTRICT: Record<TelanganaDistrictKey, keyof typeof TELANGANA_MANDAL_HOTSPOTS> = {
  adilabad: "Adilabad",
  bhadradri_kothagudem: "Bhadradri Kothagudem",
  hanumakonda: "Hanumakonda",
  hyderabad: "Hyderabad",
  jagtial: "Jagtial",
  jangaon: "Jangaon",
  jayashankar_bhupalpally: "Jayashankar Bhupalpally",
  jogulamba_gadwal: "Jogulamba Gadwal",
  kamareddy: "Kamareddy",
  karimnagar: "Karimnagar",
  khammam: "Khammam",
  komaram_bheem_asifabad: "Kumurambheem Asifabad",
  mahabubabad: "Mahabubabad",
  mahbubnagar: "Mahabubnagar",
  mancherial: "Mancherial",
  medak: "Medak",
  medchal_malkajgiri: "Medchal Malkajgiri",
  mulugu: "Mulugu",
  nagarkurnool: "Nagarkurnool",
  nalgonda: "Nalgonda",
  narayanpet: "Narayanpet",
  nirmal: "Nirmal",
  nizamabad: "Nizamabad",
  peddapalli: "Peddapalli",
  rajanna_sircilla: "Rajanna Sircilla",
  rangareddy: "Rangareddy",
  sangareddy: "Sangareddy",
  siddipet: "Siddipet",
  suryapet: "Suryapet",
  vikarabad: "Vikarabad",
  wanaparthy: "Wanaparthy",
  warangal: "Warangal",
  yadadri_bhuvanagiri: "Yadadri Bhuvanagiri",
};

/** SVG mandal boundaries for a district (same coordinate space as the state map). */
export function mandalHotspotsForDistrict(districtKey: TelanganaDistrictKey): MandalHotspot[] | null {
  const bundleKey = MANDAL_HOTSPOT_KEY_BY_DISTRICT[districtKey];
  const rows = bundleKey ? TELANGANA_MANDAL_HOTSPOTS[bundleKey] : null;
  return rows && rows.length > 0 ? rows : null;
}

export function mandalCountForDistrict(districtKey: TelanganaDistrictKey): number {
  return mandalHotspotsForDistrict(districtKey)?.length ?? 0;
}

/** Rough bounds from M/L polygon paths (mandal hotspots use district-local coords, not the state map). */
export function pathBounds(d: string): { minX: number; minY: number; maxX: number; maxY: number } | null {
  const nums: number[] = [];
  const re = /[-+]?(?:\d*\.\d+|\d+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(d)) !== null) {
    nums.push(Number(m[0]));
  }
  if (nums.length < 4) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i + 1 < nums.length; i += 2) {
    const x = nums[i];
    const y = nums[i + 1];
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  if (!Number.isFinite(minX)) return null;
  return { minX, minY, maxX, maxY };
}

export function pathCenter(d: string): { x: number; y: number } | null {
  const b = pathBounds(d);
  if (!b) return null;
  return { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 };
}

/** Break long mandal names into 1–2 lines for map labels. */
export function mandalLabelLines(title: string, maxCharsPerLine = 14): string[] {
  const t = title.trim();
  if (!t) return [];
  if (t.length <= maxCharsPerLine) return [t];
  const words = t.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    const mid = Math.ceil(words.length / 2);
    const a = words.slice(0, mid).join(" ");
    const b = words.slice(mid).join(" ");
    if (a.length <= maxCharsPerLine && b.length <= maxCharsPerLine) return [a, b];
  }
  if (t.length <= maxCharsPerLine + 6) return [t];
  const breakAt = t.lastIndexOf(" ", maxCharsPerLine);
  if (breakAt > 5) return [t.slice(0, breakAt), t.slice(breakAt + 1)];
  const half = Math.ceil(t.length / 2);
  return [t.slice(0, half), t.slice(half)];
}

function abbreviateMandalTitle(title: string, maxChars: number): string {
  if (maxChars < 4) return "";
  const t = title.trim();
  if (t.length <= maxChars) return t;
  if (maxChars >= 7) return `${t.slice(0, maxChars - 1)}…`;
  return t.slice(0, maxChars);
}

const CHAR_WIDTH_EM = 0.52;
const LINE_HEIGHT_EM = 1.12;

export type MandalLabelLayout = {
  lines: string[];
  fontSize: number;
  hide: boolean;
};

function textFitsBox(lines: string[], fontSize: number, boxW: number, boxH: number): boolean {
  const maxLen = Math.max(...lines.map((l) => l.length), 1);
  const textW = maxLen * fontSize * CHAR_WIDTH_EM;
  const textH = lines.length * fontSize * LINE_HEIGHT_EM;
  return textW <= boxW * 0.82 && textH <= boxH * 0.78;
}

export type MandalLabelLayoutOptions = {
  /** Upper bound in SVG user units (should scale with viewBox width). */
  maxFontSize?: number;
  minFontSize?: number;
};

/** Size label to fit inside mandal polygon bounds (SVG user units). */
export function mandalLabelLayout(
  title: string,
  boxW: number,
  boxH: number,
  opts: MandalLabelLayoutOptions = {},
): MandalLabelLayout {
  const maxFontCap = opts.maxFontSize ?? 9;
  const minFont = opts.minFontSize ?? 2.5;

  if (boxW < 14 || boxH < 10 || Math.min(boxW, boxH) < 8) {
    return { lines: [], fontSize: 0, hide: true };
  }

  const fitW = boxW * 0.88;
  const fitH = boxH * 0.86;
  const maxChars = Math.max(3, Math.floor(fitW / (CHAR_WIDTH_EM * 4.2)));
  let lines = mandalLabelLines(title, maxChars);

  const boxMaxFromSize = Math.min(fitW, fitH) * 0.26;
  const maxFont = Math.min(maxFontCap, boxMaxFromSize);

  let fontSize = Math.min(
    fitH * 0.3,
    fitW / (Math.max(...lines.map((l) => l.length), 1) * CHAR_WIDTH_EM + 0.55),
    fitH / (lines.length * LINE_HEIGHT_EM + 0.3),
    maxFont,
  );
  fontSize = Math.max(minFont, fontSize);

  while (fontSize > minFont && !textFitsBox(lines, fontSize, fitW, fitH)) {
    fontSize -= 0.2;
  }

  if (!textFitsBox(lines, fontSize, fitW, fitH)) {
    const short = abbreviateMandalTitle(title, Math.max(3, Math.floor(fitW / (fontSize * CHAR_WIDTH_EM + 0.2))));
    if (short) lines = [short];
    while (fontSize > minFont && !textFitsBox(lines, fontSize, fitW, fitH)) {
      fontSize -= 0.2;
    }
  }

  if (!textFitsBox(lines, fontSize, fitW, fitH) || fontSize < minFont + 0.05) {
    return { lines: [], fontSize: 0, hide: true };
  }

  return { lines, fontSize, hide: false };
}

/** SVG viewBox string to frame all mandals for a district (separate image-map coordinate space). */
export function mandalViewBoxForDistrict(districtKey: TelanganaDistrictKey, pad = 14): string | null {
  const mandals = mandalHotspotsForDistrict(districtKey);
  if (!mandals?.length) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const row of mandals) {
    const b = pathBounds(row.d);
    if (!b) continue;
    if (b.minX < minX) minX = b.minX;
    if (b.minY < minY) minY = b.minY;
    if (b.maxX > maxX) maxX = b.maxX;
    if (b.maxY > maxY) maxY = b.maxY;
  }
  if (!Number.isFinite(minX)) return null;
  const w = maxX - minX + pad * 2;
  const h = maxY - minY + pad * 2;
  return `${minX - pad} ${minY - pad} ${w} ${h}`;
}

/** @internal — verify all configured districts resolve (dev sanity). */
export function assertMandalCoverage(): void {
  for (const d of TELANGANA_DISTRICTS) {
    if (!mandalHotspotsForDistrict(d.key)) {
      throw new Error(`Missing mandal hotspots for district: ${d.key}`);
    }
  }
}
